import { useMutation, useQuery, useQueryClient } from '@tanstack/react-query';
import { mailMigrationSourceIsPinned } from '@cpm/shared';
import type {
  MailMigrationDto,
  MailMigrationAuthMode,
  MailMigrationMxPlanDto,
  MailMigrationRuleReviewDto,
  MailMigrationItemDto,
  MailMigrationItemState,
  MailMigrationPlanDto,
  MailMigrationStep,
  SubscriptionMailMigrationDto,
} from '@cpm/shared';
import { HardDriveDownload, Play, Plus } from 'lucide-react';
import { useMemo, useState } from 'react';
import { api } from '../api/client.js';
import { useMe } from '../api/auth.js';
import { HelpLink } from './help.js';
import { Modal } from './Modal.js';
import {
  Badge,
  Button,
  Card,
  CardHeader,
  EmptyState,
  ErrorNote,
  Input,
  Label,
  MeterBar,
  Select,
  Spinner,
  type BadgeTone,
} from './ui.js';

/**
 * Mailbox migration panel (G13, ADR-0023/0039): per-subscription staged
 * migration from a frozen Plesk inventory, including rule dispositions,
 * preflight gates, resumable content sync, equality checks and DNS cutover.
 */

function itemTone(state: MailMigrationItemState): BadgeTone {
  switch (state) {
    case 'verified':
      return 'green';
    case 'failed':
      return 'red';
    case 'syncing':
    case 'synced':
    case 'provisioned':
      return 'blue';
    default:
      return 'gray';
  }
}

function migrationTone(status: MailMigrationDto['status']): BadgeTone {
  switch (status) {
    case 'completed':
      return 'green';
    case 'failed':
      return 'red';
    case 'running':
      return 'blue';
    case 'cancelled':
      return 'gray';
    default:
      return 'yellow';
  }
}

function formatBytes(bytes: number | null): string {
  if (bytes === null) return '—';
  if (bytes >= 1024 * 1024 * 1024) return `${(bytes / (1024 * 1024 * 1024)).toFixed(1)} GB`;
  if (bytes >= 1024 * 1024) return `${(bytes / (1024 * 1024)).toFixed(1)} MB`;
  if (bytes >= 1024) return `${(bytes / 1024).toFixed(1)} KB`;
  return `${bytes} B`;
}

// ── Start form ──────────────────────────────────────────────────────

function StartForm({
  subscriptionId,
  plan,
  onDone,
}: {
  subscriptionId: string;
  plan: MailMigrationPlanDto;
  onDone: () => void;
}) {
  const queryClient = useQueryClient();
  const me = useMe();
  const selectable = plan.items.filter((i) => i.action !== 'unsupported');
  const [selected, setSelected] = useState<Set<string>>(
    () => new Set(selectable.map((i) => i.address)),
  );
  const rules = useMemo(
    () => [...plan.items.flatMap((item) => item.rules), ...plan.domainRules],
    [plan],
  );
  const [selectedRules, setSelectedRules] = useState<Set<string>>(
    () => new Set(rules.filter((rule) => rule.selected).map((rule) => rule.key)),
  );
  const [authMode, setAuthMode] = useState<MailMigrationAuthMode>('master');
  const [sourceSshPin, setSourceSshPin] = useState('');
  const [masterUser, setMasterUser] = useState('');
  const [masterPassword, setMasterPassword] = useState('');
  const [passwordsText, setPasswordsText] = useState('');
  const [sourceHost, setSourceHost] = useState('');
  const [sourceAddress, setSourceAddress] = useState('');
  const [maxBytes, setMaxBytes] = useState('');
  const [concurrency, setConcurrency] = useState('1');

  const start = useMutation({
    mutationFn: () => {
      const passwords: Record<string, string> = {};
      for (const line of passwordsText.split('\n')) {
        const [address, ...rest] = line.trim().split(/\s+/);
        if (address && rest.length > 0) passwords[address.toLowerCase()] = rest.join(' ');
      }
      return api.post(`/api/mail/subscriptions/${subscriptionId}/migration`, {
        authMode,
        ...(authMode === 'master'
          ? { masterUser, masterPassword }
          : authMode === 'ssh' ? { sourceSshHostKeySha256: sourceSshPin.trim() } : { passwords }),
        ...(sourceHost.trim() !== '' ? { sourceHost: sourceHost.trim() } : {}),
        ...(sourceAddress.trim() !== '' ? { sourceAddress: sourceAddress.trim() } : {}),
        ...(maxBytes.trim() !== '' ? { maxBytesPerSecond: Number(maxBytes) } : {}),
        concurrency: Number(concurrency),
        addresses: [...selected],
        selectedRuleKeys: rules
          .filter((rule) => selectedRules.has(rule.key) && (rule.address === null || selected.has(rule.address)))
          .map((rule) => rule.key),
      });
    },
    onSuccess: () => {
      void queryClient.invalidateQueries({ queryKey: ['mail-migration'] });
      onDone();
    },
  });

  return (
    <form
      className="space-y-4"
      onSubmit={(e) => {
        e.preventDefault();
        start.mutate();
      }}
    >
      <div>
        <Label htmlFor="mig-mailboxes">Mailboxes ({selected.size} of {selectable.length} selected)</Label>
        <div className="max-h-48 overflow-auto rounded-lg border border-slate-200 p-2 dark:border-slate-800">
          {plan.items.map((item) => (
            <label
              key={item.address}
              className={`flex items-center gap-2 rounded px-2 py-1 text-sm ${
                item.action === 'unsupported' ? 'opacity-50' : ''
              }`}
            >
              <input
                type="checkbox"
                disabled={item.action === 'unsupported'}
                checked={selected.has(item.address)}
                onChange={(e) => {
                  const next = new Set(selected);
                  if (e.target.checked) next.add(item.address);
                  else next.delete(item.address);
                  setSelected(next);
                }}
              />
              <span className="font-mono">{item.address}</span>
              <Badge tone={item.action === 'create' ? 'blue' : item.action === 'forwarder' ? 'gray' : item.action === 'unsupported' ? 'red' : 'green'}>
                {item.action}
              </Badge>
              <span className="text-xs text-slate-500">{formatBytes(item.usedBytes)}</span>
              {item.warnings.length > 0 && (
                <span className="truncate text-xs text-amber-600" title={item.warnings.join(' ')}>
                  {item.warnings[0]}
                </span>
              )}
            </label>
          ))}
        </div>
      </div>

      <div>
        <Label htmlFor="mig-rules">
          Address and policy rules ({rules.filter((rule) => selectedRules.has(rule.key) && (rule.address === null || selected.has(rule.address))).length} selected)
        </Label>
        <div id="mig-rules" className="max-h-56 space-y-1 overflow-auto rounded-lg border border-slate-200 p-2 dark:border-slate-800">
          {rules.length === 0 ? (
            <p className="px-2 py-1 text-sm text-slate-500">No source rules were collected.</p>
          ) : rules.map((rule) => {
            const supported = rule.disposition === 'create' || rule.disposition === 'exists';
            const mailboxSelected = rule.address === null || selected.has(rule.address);
            return (
              <label
                key={rule.key}
                className={`block rounded px-2 py-1.5 text-sm ${supported && mailboxSelected ? '' : 'opacity-60'}`}
              >
                <span className="flex items-center gap-2">
                  <input
                    type="checkbox"
                    disabled={!supported || !mailboxSelected}
                    checked={supported && mailboxSelected && selectedRules.has(rule.key)}
                    onChange={(event) => {
                      const next = new Set(selectedRules);
                      if (event.target.checked) next.add(rule.key);
                      else next.delete(rule.key);
                      setSelectedRules(next);
                    }}
                  />
                  <span className="font-medium">{rule.type.replace('_', ' ')}</span>
                  <Badge tone={rule.disposition === 'create' ? 'blue' : rule.disposition === 'exists' ? 'green' : rule.disposition === 'manual' ? 'yellow' : 'red'}>
                    {rule.disposition}
                  </Badge>
                  <span className="truncate font-mono text-xs text-slate-500" title={rule.key}>
                    {rule.address ?? rule.key}
                  </span>
                </span>
                <span className="ml-6 block text-xs text-slate-500 dark:text-slate-400">{rule.detail}</span>
              </label>
            );
          })}
        </div>
        <p className="mt-1 text-xs text-slate-500 dark:text-slate-400">
          Manual and unsupported rules stay in the migration record as explicit operator dispositions; they are never silently approximated.
        </p>
      </div>

      <div>
        <Label htmlFor="mig-auth">Source IMAP authentication</Label>
        <Select id="mig-auth" value={authMode} onChange={(e) => setAuthMode(e.target.value as MailMigrationAuthMode)}>
          <option value="master">Dovecot master user (recommended)</option>
          <option value="per_mailbox">Per-mailbox passwords</option>
          {me.data?.role === 'admin' && <option value="ssh">Read-only Plesk SSH access</option>}
        </Select>
        <p className="mt-1 text-xs text-slate-500 dark:text-slate-400">
          {authMode === 'master' ? (
            <>
              One credential for every mailbox — set up a Dovecot master user on the Plesk server
              first (see <HelpLink topic="mail/migration">Migrating mailboxes</HelpLink>).
            </>
          ) : authMode === 'ssh' ? (
            'Use the configured Plesk SSH key to read selected mailboxes without their passwords. Requires administrator access and a verified source SSH host key.'
          ) : (
            'Paste one line per mailbox: address password.'
          )}
        </p>
      </div>
      {authMode === 'master' ? (
        <div className="grid grid-cols-2 gap-3">
          <div>
            <Label htmlFor="mig-master-user">Master user</Label>
            <Input
              id="mig-master-user"
              required
              placeholder="cpm-migration"
              value={masterUser}
              onChange={(e) => setMasterUser(e.target.value)}
            />
          </div>
          <div>
            <Label htmlFor="mig-master-pass">Master password</Label>
            <Input
              id="mig-master-pass"
              type="password"
              required
              autoComplete="off"
              value={masterPassword}
              onChange={(e) => setMasterPassword(e.target.value)}
            />
          </div>
        </div>
      ) : authMode === 'ssh' ? (
        <div>
          <Label htmlFor="mig-ssh-pin">Verified source SSH host-key SHA256 (hex)</Label>
          <Input id="mig-ssh-pin" required pattern="[a-f0-9]{64}" value={sourceSshPin} onChange={(event) => setSourceSshPin(event.target.value)} />
          <p className="mt-1 text-xs text-slate-500">Obtain the fingerprint through a trusted channel. Source messages and passwords remain unchanged.</p>
        </div>
      ) : (
        <div>
          <Label htmlFor="mig-passwords">Passwords</Label>
          <textarea
            id="mig-passwords"
            required
            rows={4}
            className="block w-full rounded-lg border-0 bg-white px-3 py-2 font-mono text-sm text-slate-900 shadow-sm ring-1 ring-inset ring-slate-300 placeholder:text-slate-400 focus:ring-2 focus:ring-inset focus:ring-brand-600 dark:bg-slate-800 dark:text-white dark:ring-slate-600"
            placeholder={'alice@example.com secret1\nbob@example.com secret2'}
            value={passwordsText}
            onChange={(e) => setPasswordsText(e.target.value)}
          />
        </div>
      )}

      <div className="grid grid-cols-3 gap-3">
        <div>
          <Label htmlFor="mig-host">Source host (optional)</Label>
          <Input
            id="mig-host"
            placeholder="Plesk host default"
            value={sourceHost}
            onChange={(e) => setSourceHost(e.target.value)}
          />
        </div>
        <div>
          <Label htmlFor="mig-throttle">Max bytes/s per mailbox</Label>
          <Input
            id="mig-throttle"
            type="number"
            min={1024}
            placeholder="unthrottled"
            value={maxBytes}
            onChange={(e) => setMaxBytes(e.target.value)}
          />
        </div>
        <div>
          <Label htmlFor="mig-conc">Parallel mailboxes</Label>
          <Select id="mig-conc" value={concurrency} onChange={(e) => setConcurrency(e.target.value)}>
            <option value="1">1</option>
            <option value="2">2</option>
            <option value="3">3</option>
          </Select>
        </div>
      </div>

      <div>
        <Label htmlFor="mig-source-address">{authMode === 'ssh' ? 'Source server IP (required)' : 'Source server IP (required before cutover)'}</Label>
        <Input id="mig-source-address" required={authMode === 'ssh'} value={sourceAddress} onChange={(event) => setSourceAddress(event.target.value)} placeholder="Verified Plesk IPv4 or IPv6 address" />
        <p className="mt-1 text-xs text-slate-500">{authMode === 'ssh' ? 'Must match the Plesk SSH host or one of its current DNS addresses. Copies use encrypted SSH with the verified host key.' : 'Copies connect to this fixed address. The source hostname still identifies its TLS certificate.'}</p>
      </div>
      {start.error != null && <ErrorNote error={start.error} />}
      <div className="flex justify-end">
        <Button type="submit" loading={start.isPending} disabled={selected.size === 0}>
          <Play className="size-4" /> Create staged migration
        </Button>
      </div>
    </form>
  );
}

// ── Migration display ───────────────────────────────────────────────

function ItemRow({ item }: { item: MailMigrationItemDto }) {
  const total = item.sourceMessages;
  const done = item.state === 'verified' ? total : item.transferredMessages;
  return (
    <li className="px-5 py-2.5 text-sm">
      <div className="flex flex-wrap items-center justify-between gap-2">
        <div className="flex min-w-0 items-center gap-2">
          <span className="truncate font-mono">{item.address}</span>
          <Badge tone={itemTone(item.state)}>{item.state}</Badge>
          {item.attempts > 1 && (
            <span className="text-xs text-slate-500">attempt {item.attempts}</span>
          )}
        </div>
        <div className="flex items-center gap-2">
          {total != null && item.state !== 'skipped' && (
            <>
              <div className="w-32">
                <MeterBar value={done ?? 0} max={total || 1} warnAt={2} dangerAt={2} />
              </div>
              <span className="text-xs tabular-nums text-slate-500 dark:text-slate-400">
                {done ?? 0}/{total} msgs
              </span>
            </>
          )}
          {item.state === 'verified' && (
            <span className="text-xs text-slate-500 dark:text-slate-400">
              {item.destMessages} msgs · {formatBytes(item.destBytes)} verified
            </span>
          )}
        </div>
      </div>
      {item.lastError && (
        <p className="mt-1 text-xs text-red-600 dark:text-red-400">{item.lastError}</p>
      )}
    </li>
  );
}

const MIGRATION_STEPS: Array<{ step: MailMigrationStep; label: string }> = [
  { step: 'provision', label: 'Provision' },
  { step: 'preflight', label: 'Preflight' },
  { step: 'sync', label: 'Initial sync' },
  { step: 'verify', label: 'Verify' },
  { step: 'activate_rules', label: 'Activate rules' },
  { step: 'final_sync', label: 'Final sync' },
  { step: 'cutover', label: 'Publish cutover' },
  { step: 'catch_up', label: 'Catch up late mail' },
  { step: 'complete', label: 'Complete migration' },
];

function stepAllowed(migration: MailMigrationDto, step: MailMigrationStep): boolean {
  const complete = new Set(migration.completedSteps);
  if (migration.cutoverAt && !['verify', 'catch_up', 'complete'].includes(step)) return false;
  switch (step) {
    case 'provision': return true;
    case 'preflight': return complete.has('provision');
    case 'sync': return complete.has('preflight');
    case 'verify': return complete.has('sync') || complete.has('final_sync');
    case 'activate_rules': return complete.has('provision') && complete.has('preflight') && complete.has('verify');
    case 'final_sync': return complete.has('sync') && complete.has('verify') && complete.has('activate_rules');
    case 'cutover': return mailMigrationSourceIsPinned(migration.config) && !!migration.acceptance?.passwordsVerified && migration.acceptance.webmailVerified && complete.has('preflight') && complete.has('activate_rules') && complete.has('final_sync') && complete.has('verify');
    case 'catch_up': return complete.has('cutover');
    case 'complete': return complete.has('catch_up') && complete.has('verify') && !!migration.acceptance?.deliveryVerified && !!migration.acceptance.clientFoldersVerified;
  }
}

function MigrationSourceForm({ migration, onSaved }: { migration: MailMigrationDto; onSaved: () => void }) {
  const [address, setAddress] = useState(migration.config.sourceAddress ?? '');
  const save = useMutation({
    mutationFn: () => api.put(`/api/mail/migrations/${migration.id}/source`, { sourceAddress: address.trim() }),
    onSuccess: onSaved,
  });
  return <form className="space-y-2 px-5 py-3" onSubmit={(event) => { event.preventDefault(); save.mutate(); }}>
    <Label htmlFor={`source-${migration.id}`}>Source server IP for {migration.config.sourceHost}</Label>
    <Input id={`source-${migration.id}`} value={address} onChange={(event) => setAddress(event.target.value)} required placeholder="Verified Plesk IPv4 or IPv6 address" />
    <p className="text-xs text-slate-500">Pin the source before moving mail DNS. Changing this address requires fresh preflight, sync and verification; mailbox passwords and rules stay intact.</p>
    <Button variant="secondary" loading={save.isPending} type="submit">Save source IP</Button>
    {save.error && <ErrorNote error={save.error} />}
  </form>;
}

function MigrationAcceptanceForm({ migration, onSaved }: { migration: MailMigrationDto; onSaved: () => void }) {
  const [checks, setChecks] = useState(migration.acceptance ?? {
    passwordsVerified: false, webmailVerified: false, deliveryVerified: false, clientFoldersVerified: false, sourceQuietSince: null,
  });
  const save = useMutation({
    mutationFn: () => api.put(`/api/mail/migrations/${migration.id}/acceptance`, checks),
    onSuccess: onSaved,
  });
  return <form className="space-y-2 px-5 py-3" onSubmit={(event) => { event.preventDefault(); save.mutate(); }}>
    <p className="text-sm font-medium">Administrator acceptance</p>
    {([
      ['passwordsVerified', 'Existing passwords work for mailbox and sending access'],
      ['webmailVerified', 'Webmail login, profiles and identities verified'],
      ['deliveryVerified', 'External incoming and outgoing mail verified'],
      ['clientFoldersVerified', 'After handoff, nested messages and folder moves agree in desktop clients and webmail'],
    ] as const).map(([key, label]) => <label key={key} className="flex items-center gap-2 text-sm">
      <input type="checkbox" checked={checks[key] ?? false} disabled={key === 'clientFoldersVerified' && !migration.cutoverAt} onChange={(event) => setChecks({ ...checks, [key]: event.target.checked })} />{label}
    </label>)}
    <p className="text-xs text-slate-500">An old IMAP prefix or cached folder separator can hide messages or create separate dot-named folders. Open an older nested message and verify a test move in both clients before accepting the handoff. A populated sidebar is not enough.</p>
    {migration.cutoverAt && <label className="block text-sm">No source deliveries since (UTC, from delivery logs)
      <Input type="datetime-local" value={checks.sourceQuietSince?.slice(0, 16) ?? ''}
        onChange={(event) => setChecks({ ...checks, sourceQuietSince: event.target.value ? new Date(`${event.target.value}Z`).toISOString() : null })} />
    </label>}
    <Button variant="secondary" loading={save.isPending} type="submit">Save acceptance</Button>
    {save.error && <ErrorNote error={save.error} />}
  </form>;
}

function ruleTone(state: MailMigrationDto['rules'][number]['state']): BadgeTone {
  if (state === 'verified') return 'green';
  if (state === 'failed' || state === 'unsupported') return 'red';
  if (state === 'active') return 'blue';
  if (state === 'manual') return 'yellow';
  return 'gray';
}

function MigrationMxReview({ migration, onSaved }: { migration: MailMigrationDto; onSaved: () => void }) {
  const [mxTarget, setMxTarget] = useState(migration.mxCutover?.plan.mxTarget ?? '');
  const editable = ['planned', 'waiting', 'failed'].includes(migration.status) && !migration.cutoverAt && !migration.mxCutover?.appliedAt;
  const preview = useMutation({ mutationFn: () => api.post<MailMigrationMxPlanDto>(`/api/mail/migrations/${migration.id}/mx-cutover/preview`, { mxTarget: mxTarget.trim() || null }) });
  const approve = useMutation({
    mutationFn: (plan: MailMigrationMxPlanDto) => api.put(`/api/mail/migrations/${migration.id}/mx-cutover`, { digest: plan.digest, mxTarget: plan.mxTarget ?? null }),
    onSuccess: () => { onSaved(); preview.reset(); },
  });
  const plan = preview.data;
  return <div className="border-t border-slate-200 px-5 py-3 dark:border-slate-800">
    <p className="mb-2 text-sm font-medium">Existing MX records</p>
    <p className="mb-2 text-xs text-slate-500">Review replacements for imported or custom mail routing. Approval saves the reviewed values; only Publish cutover changes DNS. Edited records require a new review.</p>
    {migration.mxCutover && <p className="mb-2 text-sm">{migration.mxCutover.appliedAt ? 'Reviewed MX replacement applied; rollback records retained.' : 'Saved MX review retained. Cutover requires it to match current DNS.'}</p>}
    <label className="mb-3 block text-sm">Destination MX hostname (optional)
      <Input className="mt-1" value={mxTarget} disabled={!editable || preview.isPending || approve.isPending}
        placeholder="Use the mail server default" onChange={event => { setMxTarget(event.target.value); preview.reset(); approve.reset(); }} />
    </label>
    <p className="mb-2 text-xs text-slate-500">Applies to domains created by this migration and their selected native aliases. Prepare direct A/AAAA records and SMTP TLS for this hostname before cutover. Blank uses the mail server default. Client settings and certificates require their own handoff.</p>
    {editable && <Button variant="secondary" loading={preview.isPending} onClick={() => preview.mutate()}>Review MX replacements</Button>}
    {(preview.error || approve.error) && <ErrorNote error={preview.error ?? approve.error} />}
    {plan && <div className="mt-3 space-y-2">
      <p className="text-sm">Reviewed target: {plan.mxTarget ?? 'Mail server default'}</p>
      {plan.blockers.map((blocker, i) => <p key={i} className="text-sm text-red-600">{blocker}</p>)}
      {plan.changes.length === 0 ? <p className="text-sm">No managed user MX records need replacement. External DNS and other mail records retain their separate checks.</p> : <><div className="space-y-3 sm:hidden">{plan.changes.map(change => <div key={`${change.zoneId}:${change.name}`} className="rounded border border-slate-200 p-3 text-sm dark:border-slate-700">
        <p className="break-all font-medium">{change.name === '@' ? change.zoneName : `${change.name}.${change.zoneName}`}</p>
        <p className="mt-2 text-xs text-slate-500">Current MX / TTL</p>
        {change.before.map(r => <p key={r.id} className="break-all font-mono">{r.content} / {r.ttl}s</p>)}
        <p className="mt-2 text-xs text-slate-500">Destination MX / TTL</p>
        {change.after.map(r => <p key={r.id} className="break-all font-mono">{r.content} / {r.ttl}s</p>)}
      </div>)}</div><div className="hidden overflow-x-auto sm:block"><table className="w-full text-left text-sm">
        <thead><tr><th className="p-2">DNS name</th><th className="p-2">Current MX / TTL</th><th className="p-2">Destination MX / TTL</th></tr></thead>
        <tbody>{plan.changes.map(change => <tr key={`${change.zoneId}:${change.name}`}>
          <td className="p-2">{change.name === '@' ? change.zoneName : `${change.name}.${change.zoneName}`}</td>
          <td className="p-2 font-mono">{change.before.map(r => <div key={r.id}>{r.content} / {r.ttl}s</div>)}</td>
          <td className="p-2 font-mono">{change.after.map(r => <div key={r.id}>{r.content} / {r.ttl}s</div>)}</td>
        </tr>)}</tbody>
      </table></div></>}
      {editable && plan.blockers.length === 0 && <Button loading={approve.isPending} onClick={() => approve.mutate(plan)}>Approve these MX replacements for cutover</Button>}
    </div>}
  </div>;
}

function AutoresponderReview({ migrationId, ruleId, onSaved }: { migrationId: string; ruleId: string; onSaved: () => void }) {
  const [open, setOpen] = useState(false);
  const path = `/api/mail/migrations/${migrationId}/rules/${ruleId}/review`;
  const preview = useMutation({ mutationFn: () => api.get<MailMigrationRuleReviewDto>(path) });
  const approve = useMutation({ mutationFn: (digest: string) => api.put(path, { digest }),
    onSuccess: () => { setOpen(false); onSaved(); } });
  const plan = preview.data;
  const summary = (source: Record<string, unknown>) => {
    const frequency = source.replyFrequency as { repliesPerSenderPerDay?: number; trackedSenderLimit?: number } | undefined;
    return <dl className="space-y-2 break-words text-sm">
      <div><dt className="text-slate-500">Subject</dt><dd>{String(source.subject ?? '(unchanged subject)')}</dd></div>
      <div><dt className="text-slate-500">Format</dt><dd>{String(source.contentType ?? 'text/plain')} / {String(source.charset ?? 'unspecified')}</dd></div>
      <div><dt className="text-slate-500">Replies per sender per day</dt><dd>{frequency?.repliesPerSenderPerDay ?? 'Not collected'}</dd></div>
      <div><dt className="text-slate-500">Source sender tracking capacity</dt><dd>{frequency?.trackedSenderLimit ?? 'Not collected'} (retained as evidence)</dd></div>
      <div><dt className="text-slate-500">Subject filter / End date / Forward copy</dt><dd>{String(source.requestText ?? 'Any subject')} / {String(source.endDate ?? 'No end date')} / {String(source.forwardAddress ?? 'None')}</dd></div>
      <div><dt className="text-slate-500">Reply text (HTML shown as text)</dt><dd className="max-h-56 overflow-auto whitespace-pre-wrap rounded border border-slate-200 p-2 dark:border-slate-700">{String(source.responseText ?? '(unavailable)')}</dd></div>
    </dl>;
  };
  return <>
    <Button variant="secondary" onClick={() => { setOpen(true); approve.reset(); preview.reset(); preview.mutate(); }}>Review updated autoresponder</Button>
    <Modal title="Review updated autoresponder" open={open} wide onClose={() => { if (!approve.isPending) setOpen(false); }}>
      <p className="mb-4 text-sm text-slate-500">Keep the original rule as history and stage a supported replacement from the latest complete source inventory. Approval does not activate replies or change DNS. Run preflight, verification, rule activation, final sync and verification again before cutover.</p>
      {(preview.error || approve.error) && <ErrorNote error={preview.error ?? approve.error} />}
      {preview.isPending && <Spinner />}
      {plan && <div className="space-y-4">
        <p className="text-sm">Source inventory: {plan.snapshotAt ? new Date(plan.snapshotAt).toLocaleString() : 'Unavailable'}</p>
        {plan.blockers.map((text, i) => <p key={i} className="text-sm text-red-600">{text}</p>)}
        <div className="grid gap-4 sm:grid-cols-2"><section><h4 className="mb-2 font-medium">Original frozen rule</h4>{summary(plan.current.source)}</section>
          <section><h4 className="mb-2 font-medium">Proposed replacement</h4>{plan.proposed ? summary(plan.proposed.source) : <p>No matching source rule.</p>}</section></div>
        {plan.proposed && <p className="text-sm text-slate-500">{plan.proposed.detail}</p>}
        {plan.blockers.length === 0 && plan.proposed && <Button loading={approve.isPending} onClick={() => approve.mutate(plan.digest)}>Approve replacement plan</Button>}
      </div>}
    </Modal>
  </>;
}

function MigrationBlock({
  migration,
}: {
  migration: MailMigrationDto;
}) {
  const queryClient = useQueryClient();
  const me = useMe();
  const invalidate = () => void queryClient.invalidateQueries({ queryKey: ['mail-migration'] });
  const resume = useMutation({
    mutationFn: () => api.post(`/api/mail/migrations/${migration.id}/resume`, {}),
    onSuccess: invalidate,
  });
  const cancel = useMutation({
    mutationFn: () => api.post(`/api/mail/migrations/${migration.id}/cancel`, {}),
    onSuccess: invalidate,
  });
  const runStep = useMutation({
    mutationFn: (step: MailMigrationStep) =>
      api.post(`/api/mail/migrations/${migration.id}/steps/${step}`, {}),
    onSuccess: invalidate,
  });
  const rollbackCutover = useMutation({
    mutationFn: () => api.post(`/api/mail/migrations/${migration.id}/rollback-cutover`, {}),
    onSuccess: invalidate,
  });
  const counts = useMemo(() => {
    const map = new Map<string, number>();
    for (const item of migration.items) map.set(item.state, (map.get(item.state) ?? 0) + 1);
    return map;
  }, [migration.items]);
  const active = migration.status === 'pending' || migration.status === 'running';
  const terminal = migration.status === 'completed' || migration.status === 'cancelled';
  const mutationError = resume.error ?? cancel.error ?? runStep.error ?? rollbackCutover.error;

  return (
    <div className="border-t border-slate-200 dark:border-slate-800">
      <div className="flex flex-wrap items-center justify-between gap-2 bg-slate-50 px-5 py-3 dark:bg-slate-800/40">
        <div className="flex flex-wrap items-center gap-2 text-sm">
          <Badge tone={migrationTone(migration.status)}>{migration.status}</Badge>
          {migration.requestedStep && <Badge tone="blue">{migration.requestedStep.replace('_', ' ')}</Badge>}
          <span className="text-slate-500 dark:text-slate-400">
            {migration.items.length} mailbox(es)
            {counts.get('verified') ? ` · ${counts.get('verified')} verified` : ''}
            {counts.get('failed') ? ` · ${counts.get('failed')} failed` : ''}
            {counts.get('skipped') ? ` · ${counts.get('skipped')} skipped` : ''}
          </span>
          <span className="text-xs text-slate-400">
            {new Date(migration.createdAt).toLocaleString()}
          </span>
        </div>
        <div className="flex items-center gap-1">
          {migration.status === 'failed' && (
            <Button variant="secondary" loading={resume.isPending} onClick={() => resume.mutate()}>
              Resume
            </Button>
          )}
          {['waiting', 'failed'].includes(migration.status) && (migration.cutoverAt || migration.requestedStep === 'cutover') && (
            <Button
              variant="secondary"
              loading={rollbackCutover.isPending}
              onClick={() => {
                if (window.confirm('Have you preserved and reconciled destination mail and prepared the captured source DNS? This action restores reviewed MX records if unchanged and withdraws generated migration DNS; it does not merge mail or restore other provider records.')) {
                  rollbackCutover.mutate();
                }
              }}
            >
              Roll back cutover DNS
            </Button>
          )}
          {!terminal && !migration.cutoverAt && migration.requestedStep !== 'cutover' && (
            <Button
              variant="danger"
              loading={cancel.isPending}
              onClick={() => {
                if (window.confirm('Cancel this migration? The current mailbox sync is aborted; completed mailboxes stay migrated.')) {
                  cancel.mutate();
                }
              }}
            >
              Cancel
            </Button>
          )}
        </div>
      </div>
      {!terminal && !active && !migration.cutoverAt && me.data?.role === 'admin' && <MigrationSourceForm
        key={`${migration.id}:${migration.config.sourceAddress ?? ''}`} migration={migration} onSaved={invalidate} />}
      {!terminal && !active && !migration.cutoverAt && me.data?.role === 'admin' && <MigrationMxReview migration={migration} onSaved={invalidate} />}
      {!terminal && !active && me.data?.role === 'admin' && <MigrationAcceptanceForm
        key={`${migration.id}:${JSON.stringify(migration.acceptance)}`} migration={migration} onSaved={invalidate} />}
      {!terminal && (
        <div className="border-t border-slate-200 px-5 py-3 dark:border-slate-800">
          <p className="mb-2 text-xs text-slate-500 dark:text-slate-400">
            Publish cutover changes mail DNS. Keep catching up late mail for at least 72 hours and confirm 24 quiet hours on the source before completing. Completion removes migration credentials.
          </p>
          <div className="flex flex-wrap gap-2">
            {MIGRATION_STEPS.map(({ step: requested, label }) => {
              const complete = migration.completedSteps.includes(requested);
              const loading = runStep.isPending && runStep.variables === requested;
              return (
                <Button
                  key={requested}
                  variant="secondary"
                  disabled={active || (complete && !['catch_up', 'verify'].includes(requested)) || !stepAllowed(migration, requested)}
                  loading={loading}
                  onClick={() => {
                    if (
                      requested !== 'cutover' ||
                      window.confirm(
                        'Publish the destination MX and mail DNS now? Cutover runs a final authoritative rule/content check first and leaves Plesk unchanged if any check fails.',
                      )
                    ) {
                      runStep.mutate(requested);
                    }
                  }}
                >
                  {complete ? '✓ ' : ''}{label}
                </Button>
              );
            })}
          </div>
        </div>
      )}
      {migration.error && (
        <p className="px-5 py-2 text-xs text-red-600 dark:text-red-400">{migration.error}</p>
      )}
      {mutationError != null && (
        <div className="px-5 py-2">
          <ErrorNote error={mutationError} />
        </div>
      )}
      <ul className="divide-y divide-slate-100 dark:divide-slate-800">
        {migration.items.map((item) => (
          <ItemRow key={item.id} item={item} />
        ))}
      </ul>
      {migration.preflight.length > 0 && (
        <div className="border-t border-slate-200 px-5 py-3 dark:border-slate-800">
          <h4 className="mb-2 text-sm font-semibold">Preflight</h4>
          <div className="space-y-2">
            {migration.preflight.map((domain) => (
              <div key={domain.domain} className="rounded-lg border border-slate-200 p-2 dark:border-slate-800">
                <div className="mb-1 flex items-center gap-2 text-sm">
                  <span className="font-mono">{domain.domain}</span>
                  <Badge tone={domain.ready ? 'green' : 'red'}>{domain.ready ? 'ready' : 'blocked'}</Badge>
                </div>
                <div className="grid gap-1 sm:grid-cols-2">
                  {domain.checks.map((check, index) => (
                    <div key={`${check.key}-${index}`} className="flex items-start gap-2 text-xs">
                      <Badge tone={check.status === 'pass' ? 'green' : check.status === 'warn' ? 'yellow' : 'red'}>{check.key}</Badge>
                      <span className="text-slate-500 dark:text-slate-400">{check.detail}</span>
                    </div>
                  ))}
                </div>
              </div>
            ))}
          </div>
        </div>
      )}
      {migration.rules.length > 0 && (
        <div className="border-t border-slate-200 px-5 py-3 dark:border-slate-800">
          <h4 className="mb-2 text-sm font-semibold">Rule dispositions</h4>
          <div className="space-y-1">
            {migration.rules.map((rule) => (
              <div key={rule.id} className="flex flex-wrap items-center gap-2 text-xs">
                <Badge tone={ruleTone(rule.state)}>{rule.state}</Badge>
                <span className="font-medium">{rule.type.replace('_', ' ')}</span>
                <span className="font-mono text-slate-500">{rule.address ?? rule.key}</span>
                {rule.equality !== null && (
                  <Badge tone={rule.equality ? 'green' : 'red'}>{rule.equality ? 'equal' : 'drifted'}</Badge>
                )}
                <span className="text-slate-500 dark:text-slate-400">{rule.lastError ?? rule.detail}</span>
                {migration.rules.some(r => r.review?.replacesRuleId === rule.id) && <Badge tone="gray">retained history</Badge>}
                {rule.review && <Badge tone="blue">reviewed replacement</Badge>}
                {!terminal && !active && !migration.cutoverAt && !migration.mxCutover?.appliedAt && me.data?.role === 'admin' &&
                  rule.type === 'autoresponder' && !rule.selected && ['manual', 'unsupported'].includes(rule.disposition) &&
                  !migration.rules.some(r => r.review?.replacesRuleId === rule.id) &&
                  <AutoresponderReview migrationId={migration.id} ruleId={rule.id} onSaved={invalidate} />}
              </div>
            ))}
          </div>
        </div>
      )}
    </div>
  );
}

// ── Panel ───────────────────────────────────────────────────────────

export function SubscriptionMailMigrationPanel({ subscriptionId }: { subscriptionId: string }) {
  const [startOpen, setStartOpen] = useState(false);

  const migrations = useQuery({
    queryKey: ['mail-migration', subscriptionId],
    queryFn: () =>
      api.get<SubscriptionMailMigrationDto>(`/api/mail/subscriptions/${subscriptionId}/migration`),
    retry: false,
    refetchInterval: (query) => {
      const list = query.state.data?.migrations ?? [];
      return list.some((m) => m.status === 'pending' || m.status === 'running') ? 2000 : false;
    },
  });
  const plan = useQuery({
    queryKey: ['mail-migration', subscriptionId, 'plan'],
    queryFn: () =>
      api.get<MailMigrationPlanDto>(`/api/mail/subscriptions/${subscriptionId}/migration/plan`),
    enabled: startOpen,
  });

  // Module disabled / no access — hide like the other panels do.
  if (migrations.error) return null;
  if (migrations.isPending) return null;
  const data = migrations.data;
  // Nothing to migrate and nothing migrated: stay out of the way.
  const hasHistory = data.migrations.length > 0;
  const hasOpenMigration = data.migrations.some((migration) =>
    ['planned', 'pending', 'running', 'waiting'].includes(migration.status),
  );

  return (
    <Card>
      <CardHeader
        title="Mailbox migration"
        description="Stage this subscription's Plesk mailboxes and delivery rules for guarded Stalwart cutover"
        actions={
          !hasOpenMigration ? (
            <Button onClick={() => setStartOpen(true)}>
              <Plus className="size-4" /> {hasHistory ? 'New migration' : 'Plan migration'}
            </Button>
          ) : undefined
        }
      />
      {!hasHistory ? (
        <EmptyState
          icon={<HardDriveDownload className="size-10" />}
          title="No migrations yet"
          description="Plan a staged migration from the Plesk inventory — IMAP content is verified by counts and bytes, while selected routing and filtering rules require exact equality before DNS cutover."
        />
      ) : (
        data.migrations.map((migration) => (
          <MigrationBlock key={migration.id} migration={migration} />
        ))
      )}

      <Modal title="Plan mailbox migration" open={startOpen} onClose={() => setStartOpen(false)} wide>
        {plan.isPending ? (
          <Spinner />
        ) : plan.error ? (
          <ErrorNote error={plan.error} />
        ) : (
          <div className="space-y-4">
            {plan.data.warnings.length > 0 && (
              <div className="rounded-lg bg-amber-50 p-3 text-sm text-amber-800 dark:bg-amber-900/30 dark:text-amber-200">
                {plan.data.warnings.map((w, i) => (
                  <p key={i}>{w}</p>
                ))}
              </div>
            )}
            {plan.data.imapsyncVersion === null && (
              <div className="rounded-lg bg-red-50 p-3 text-sm text-red-800 dark:bg-red-900/30 dark:text-red-200">
                imapsync is not available on the panel host — migrations cannot run. See the
                installation docs (the panel image ships it from v0.13).
              </div>
            )}
            {plan.data.items.length === 0 ? (
              <p className="text-sm text-slate-500 dark:text-slate-400">
                The Plesk inventory has no mailboxes for this subscription.
              </p>
            ) : (
              <>
                {plan.data.domainsToCreate.length > 0 && (
                  <p className="text-sm text-slate-600 dark:text-slate-300">
                    Mail domains created during migration:{' '}
                    <span className="font-mono">{plan.data.domainsToCreate.join(', ')}</span>
                  </p>
                )}
                <StartForm
                  subscriptionId={subscriptionId}
                  plan={plan.data}
                  onDone={() => setStartOpen(false)}
                />
              </>
            )}
          </div>
        )}
      </Modal>
    </Card>
  );
}
